import { ProjetoFormData } from '../dominios/projetos/tipos/projeto'
import { toInputDate, toBackendDate, toRequiredDate, toNumber, getPropValue } from '../utils/formatUtils'

/**
 * Converte dados do backend (PascalCase) para o formato do frontend (camelCase)
 */
export function convertFromBackendFormat(backendProjeto: any): ProjetoFormData {
  const cronograma = backendProjeto.Cronograma || backendProjeto.cronograma || {}
  const orcamento = backendProjeto.Orcamento || backendProjeto.orcamento || {}
  
  // Função auxiliar para obter valor com fallback
  const get = (...props: string[]) => getPropValue(backendProjeto, ...props)
  const getCronograma = (...props: string[]) => getPropValue(cronograma, ...props)
  const getOrcamento = (...props: string[]) => getPropValue(orcamento, ...props)
  
  return {
    id: get('Id', 'id', 'IdProjeto'),
    numeroTicket: get('NumeroTicket', 'numeroTicket') || '',
    nome: get('Nome', 'nome') || '',
    descricao: get('Descricao', 'descricao') || '',
    prefClockify: get('PrefClockify', 'prefClockify') || '',
    ticketUrl: get('TicketUrl', 'ticketUrl') || 'https://momentum.movidesk.com/Ticket/Edit/',
    dataRecebimento: toInputDate(get('DataRecebimento', 'dataRecebimento')) ?? '',
    dataReferenciaValorHora: toInputDate(get('DataReferenciaValorHora', 'dataReferenciaValorHora')) ?? '',
    idProjetoClockify: get('IdProjetoClockify', 'idProjetoClockify') || '',
    
    // Códigos - converter para string
    statusCodigo: get('StatusCodigo', 'statusCodigo') != null
      ? String(get('StatusCodigo', 'statusCodigo') ?? '')
      : '',
    prioridadeCodigo: get('PrioridadeCodigo', 'prioridadeCodigo') != null
      ? String(get('PrioridadeCodigo', 'prioridadeCodigo') ?? '')
      : '',
    tipoProjetoCodigo: get('TipoProjetoCodigo', 'tipoProjetoCodigo') != null
      ? String(get('TipoProjetoCodigo', 'tipoProjetoCodigo') ?? '')
      : '',
    
    // IDs
    solicitanteId: get('SolicitanteId', 'solicitanteId') ? String(get('SolicitanteId', 'solicitanteId')) : '',
    analistaSMNId: get('AnalistaSMNId', 'analistaSMNId') || 0,
    analistaDDPId: get('AnalistaDDPId', 'analistaDDPId') || 0,
    desenvolvedorId: get('DesenvolvedorId', 'desenvolvedorId') ? String(get('DesenvolvedorId', 'desenvolvedorId')) : '',
    
    // Envios e Aprovações
    dataEnvioEstimativa: toInputDate(get('DataEnvioEstimativa', 'dataEnvioEstimativa')) ?? '',
    dataAprovacaoEstimativa: toInputDate(get('DataAprovacaoEstimativa', 'dataAprovacaoEstimativa')) ?? '',
    dataEnvioEscopo: toInputDate(get('DataEnvioEscopo', 'dataEnvioEscopo')) ?? '',
    dataAprovacaoEscopo: toInputDate(get('DataAprovacaoEscopo', 'dataAprovacaoEscopo')) ?? '',
    dataEnvioOrcamento: toInputDate(get('DataEnvioOrcamento', 'dataEnvioOrcamento')) ?? '',
    dataAprovacaoOrcamento: toInputDate(get('DataAprovacaoOrcamento', 'dataAprovacaoOrcamento')) ?? '',
    
    // Cronograma
    dataEnvio: toInputDate(getCronograma('DataEnvio', 'dataEnvio')) ?? '',
    dataEstimativaInicio: toInputDate(getCronograma('DataEstimativaInicio', 'dataEstimativaInicio')) ?? '',
    dataEstimativaFim: toInputDate(getCronograma('DataEstimativaFim', 'dataEstimativaFim')) ?? '',
    dataEscopoInicio: toInputDate(getCronograma('DataEscopoInicio', 'dataEscopoInicio')) ?? '',
    dataEscopoFim: toInputDate(getCronograma('DataEscopoFim', 'dataEscopoFim')) ?? '',
    dataOrcamentoInicio: toInputDate(getCronograma('DataOrcamentoInicio', 'dataOrcamentoInicio')) ?? '',
    dataOrcamentoFim: toInputDate(getCronograma('DataOrcamentoFim', 'dataOrcamentoFim')) ?? '',
    dataDesenvolvimentoInicio: toInputDate(getCronograma('DataDesenvolvimentoInicio', 'dataDesenvolvimentoInicio')) ?? '',
    dataDesenvolvimentoFim: toInputDate(getCronograma('DataDesenvolvimentoFim', 'dataDesenvolvimentoFim')) ?? '',
    dataTestesInternosInicio: toInputDate(getCronograma('DataTestesInternosInicio', 'dataTestesInternosInicio')) ?? '',
    dataTestesInternosFim: toInputDate(getCronograma('DataTestesInternosFim', 'dataTestesInternosFim')) ?? '',
    dataTestesProdutoInicio: toInputDate(getCronograma('DataTestesProdutoInicio', 'dataTestesProdutoInicio')) ?? '',
    dataTestesProdutoFim: toInputDate(getCronograma('DataTestesProdutoFim', 'dataTestesProdutoFim')) ?? '',
    dataHomologacaoInicio: toInputDate(getCronograma('DataHomologacaoInicio', 'dataHomologacaoInicio')) ?? '',
    dataHomologacaoFim: toInputDate(getCronograma('DataHomologacaoFim', 'dataHomologacaoFim')) ?? '',
    dataPublicacaoPreProd: toInputDate(getCronograma('DataPublicacaoPreProd', 'dataPublicacaoPreProd')) ?? '',
    dataPublicacaoProd: toInputDate(getCronograma('DataPublicacaoProd', 'dataPublicacaoProd')) ?? '',
    
    // Orçamento - converter 0 ou null para string vazia
    escopoOrcado: (() => {
      const val = getOrcamento('EscopoOrcado', 'escopoOrcado')
      return val && val !== 0 ? String(val) : ''
    })(),
    escopoGasto: (() => {
      const val = getOrcamento('EscopoGasto', 'escopoGasto')
      return val && val !== 0 ? String(val) : ''
    })(),
    escopoCobrado: (() => {
      const val = getOrcamento('EscopoCobrado', 'escopoCobrado')
      return val && val !== 0 ? String(val) : ''
    })(),
    desenvolvimentoOrcado: (() => {
      const val = getOrcamento('DesenvolvimentoOrcado', 'desenvolvimentoOrcado')
      return val && val !== 0 ? String(val) : ''
    })(),
    desenvolvimentoGasto: (() => {
      const val = getOrcamento('DesenvolvimentoGasto', 'desenvolvimentoGasto')
      return val && val !== 0 ? String(val) : ''
    })(),
    desenvolvimentoCobrado: (() => {
      const val = getOrcamento('DesenvolvimentoCobrado', 'desenvolvimentoCobrado')
      return val && val !== 0 ? String(val) : ''
    })(),
    testesOrcado: (() => {
      const val = getOrcamento('TestesOrcado', 'testesOrcado')
      return val && val !== 0 ? String(val) : ''
    })(),
    testesGasto: (() => {
      const val = getOrcamento('TestesGasto', 'testesGasto')
      return val && val !== 0 ? String(val) : ''
    })(),
    testesCobrado: (() => {
      const val = getOrcamento('TestesCobrado', 'testesCobrado')
      return val && val !== 0 ? String(val) : ''
    })(),
    homologacaoGasto: (() => {
      const val = getOrcamento('HomologacaoGasto', 'homologacaoGasto')
      return val && val !== 0 ? String(val) : ''
    })(),
    homologacaoCobrado: (() => {
      const val = getOrcamento('HomologacaoCobrado', 'homologacaoCobrado')
      return val && val !== 0 ? String(val) : ''
    })(),
    horasGastasSemTags: (() => {
      const val = getOrcamento('HorasGastasSemTags', 'horasGastasSemTags')
      return val && val !== 0 ? String(val) : ''
    })(),
    saldoFinal: (() => {
      const val = getOrcamento('SaldoFinal', 'saldoFinal') ?? get('SaldoFinal', 'saldoFinal')
      return val && val !== 0 ? String(val) : ''
    })(),
    observacao: get('Observacao', 'observacao') || '',
    possuiFaturamentoAprovado: get('PossuiFaturamentoAprovado', 'possuiFaturamentoAprovado') ?? false,
    aplicaEstimativa: get('AplicaEstimativa', 'aplicaEstimativa') ?? true,
    aplicaEscopo: get('AplicaEscopo', 'aplicaEscopo') ?? true,
    aplicaOrcamento: get('AplicaOrcamento', 'aplicaOrcamento') ?? true,
    flagEspecial: String(get('FlagEspecial', 'flagEspecial') ?? false) as any,
    tags: get('Tags', 'tags') || [],
    cronograma: cronograma && Object.keys(cronograma).length > 0 ? {
      id: getCronograma('Id', 'id'),
      projetoId: getCronograma('ProjetoId', 'projetoId') || get('Id', 'id', 'IdProjeto') || 0,
      dataEnvio: toInputDate(getCronograma('DataEnvio', 'dataEnvio')) ?? undefined,
      dataEstimativaInicio: toInputDate(getCronograma('DataEstimativaInicio', 'dataEstimativaInicio')) ?? undefined,
      dataEstimativaFim: toInputDate(getCronograma('DataEstimativaFim', 'dataEstimativaFim')) ?? undefined,
      dataEscopoInicio: toInputDate(getCronograma('DataEscopoInicio', 'dataEscopoInicio')) ?? undefined,
      dataEscopoFim: toInputDate(getCronograma('DataEscopoFim', 'dataEscopoFim')) ?? undefined,
      dataOrcamentoInicio: toInputDate(getCronograma('DataOrcamentoInicio', 'dataOrcamentoInicio')) ?? undefined,
      dataOrcamentoFim: toInputDate(getCronograma('DataOrcamentoFim', 'dataOrcamentoFim')) ?? undefined,
      dataDesenvolvimentoInicio: toInputDate(getCronograma('DataDesenvolvimentoInicio', 'dataDesenvolvimentoInicio')) ?? undefined,
      dataDesenvolvimentoFim: toInputDate(getCronograma('DataDesenvolvimentoFim', 'dataDesenvolvimentoFim')) ?? undefined,
      dataTestesInternosInicio: toInputDate(getCronograma('DataTestesInternosInicio', 'dataTestesInternosInicio')) ?? undefined,
      dataTestesInternosFim: toInputDate(getCronograma('DataTestesInternosFim', 'dataTestesInternosFim')) ?? undefined,
      dataTestesProdutoInicio: toInputDate(getCronograma('DataTestesProdutoInicio', 'dataTestesProdutoInicio')) ?? undefined,
      dataTestesProdutoFim: toInputDate(getCronograma('DataTestesProdutoFim', 'dataTestesProdutoFim')) ?? undefined,
      dataHomologacaoInicio: toInputDate(getCronograma('DataHomologacaoInicio', 'dataHomologacaoInicio')) ?? undefined,
      dataHomologacaoFim: toInputDate(getCronograma('DataHomologacaoFim', 'dataHomologacaoFim')) ?? undefined,
      dataPublicacaoPreProd: toInputDate(getCronograma('DataPublicacaoPreProd', 'dataPublicacaoPreProd')) ?? undefined,
      dataPublicacaoProd: toInputDate(getCronograma('DataPublicacaoProd', 'dataPublicacaoProd')) ?? undefined,
      podeGerarCronograma: getCronograma('PodeGerarCronograma', 'podeGerarCronograma') ?? false
    } : undefined
  }
}

/**
 * Verifica se há dados válidos no cronograma
 */
function temDadosCronograma(data: ProjetoFormData): boolean {
  return !!(
    data.dataEnvio ||
    data.dataEstimativaInicio ||
    data.dataEstimativaFim ||
    data.dataEscopoInicio ||
    data.dataEscopoFim ||
    data.dataOrcamentoInicio ||
    data.dataOrcamentoFim ||
    data.dataDesenvolvimentoInicio ||
    data.dataDesenvolvimentoFim ||
    data.dataTestesInternosInicio ||
    data.dataTestesInternosFim ||
    data.dataTestesProdutoInicio ||
    data.dataTestesProdutoFim ||
    data.dataHomologacaoInicio ||
    data.dataHomologacaoFim ||
    data.dataPublicacaoPreProd ||
    data.dataPublicacaoProd
  )
}

/**
 * Verifica se há dados válidos no orçamento
 */
function temDadosOrcamento(data: ProjetoFormData): boolean {
  // Função auxiliar para converter valor para número válido
  const converterParaNumero = (valor: any): number => {
    if (valor === null || valor === undefined || valor === '') return 0
    if (typeof valor === 'string') {
      const trimmed = valor.trim()
      if (trimmed === '') return 0
      const num = parseFloat(trimmed)
      return isNaN(num) ? 0 : num
    }
    if (typeof valor === 'number') {
      return isNaN(valor) ? 0 : valor
    }
    return 0
  }

  // Converter valores para número e verificar se são maiores que 0
  const escopoOrcado = converterParaNumero(data.escopoOrcado)
  const escopoCobrado = converterParaNumero(data.escopoCobrado)
  const desenvolvimentoOrcado = converterParaNumero(data.desenvolvimentoOrcado)
  const desenvolvimentoCobrado = converterParaNumero(data.desenvolvimentoCobrado)
  const testesOrcado = converterParaNumero(data.testesOrcado)
  const testesCobrado = converterParaNumero(data.testesCobrado)
  const homologacaoCobrado = converterParaNumero(data.homologacaoCobrado)
  const saldoFinal = converterParaNumero(data.saldoFinal)
  
  // Campos de gasto são calculados dinamicamente e não devem ser considerados para verificar se orçamento existe

  return !!(
    escopoOrcado > 0 ||
    escopoCobrado > 0 ||
    desenvolvimentoOrcado > 0 ||
    desenvolvimentoCobrado > 0 ||
    testesOrcado > 0 ||
    testesCobrado > 0 ||
    homologacaoCobrado > 0 ||
    saldoFinal > 0
  )
}

/**
 * Converte dados do frontend (camelCase) para o formato do backend (PascalCase)
 */
export function convertToBackendFormat(data: ProjetoFormData): any {
  // Criar Cronograma apenas se houver dados válidos
  const cronograma = temDadosCronograma(data) ? {
    Id: null,
    ProjetoId: data.id || 0,
    DataEnvio: toBackendDate(data.dataEnvio),
    DataEstimativaInicio: toBackendDate(data.dataEstimativaInicio),
    DataEstimativaFim: toBackendDate(data.dataEstimativaFim),
    DataEscopoInicio: toBackendDate(data.dataEscopoInicio),
    DataEscopoFim: toBackendDate(data.dataEscopoFim),
    DataOrcamentoInicio: toBackendDate(data.dataOrcamentoInicio),
    DataOrcamentoFim: toBackendDate(data.dataOrcamentoFim),
    DataDesenvolvimentoInicio: toBackendDate(data.dataDesenvolvimentoInicio),
    DataDesenvolvimentoFim: toBackendDate(data.dataDesenvolvimentoFim),
    DataTestesInternosInicio: toBackendDate(data.dataTestesInternosInicio),
    DataTestesInternosFim: toBackendDate(data.dataTestesInternosFim),
    DataTestesProdutoInicio: toBackendDate(data.dataTestesProdutoInicio),
    DataTestesProdutoFim: toBackendDate(data.dataTestesProdutoFim),
    DataHomologacaoInicio: toBackendDate(data.dataHomologacaoInicio),
    DataHomologacaoFim: toBackendDate(data.dataHomologacaoFim),
    DataPublicacaoPreProd: toBackendDate(data.dataPublicacaoPreProd),
    DataPublicacaoProd: toBackendDate(data.dataPublicacaoProd),
    DataUltimaAtualizacao: null,
    DataCriacao: null
  } : null

  // Função auxiliar para normalizar valores vazios/null/undefined para null
  const normalizarValorOrcamento = (valor: any): number | null => {
    if (valor === null || valor === undefined || valor === '') return null
    if (typeof valor === 'string' && valor.trim() === '') return null
    const num = toNumber(valor, null)
    return num !== null && !isNaN(num) ? num : null
  }

  // Criar Orcamento:
  // - Se for criação (!data.id): só criar se houver dados válidos
  // - Se for atualização (data.id): sempre enviar, mesmo que todos os campos sejam null (permite limpar completamente)
  const isAtualizacao = !!data.id
  const temDados = temDadosOrcamento(data)
  const deveEnviarOrcamento = isAtualizacao || temDados
  
  const orcamento = deveEnviarOrcamento ? {
    Id: null,
    ProjetoId: data.id || 0,
    // Normalizar valores vazios/null/undefined para null (permitir limpar campos)
    EscopoOrcado: normalizarValorOrcamento(data.escopoOrcado),
    // Campos de gasto removidos - são calculados dinamicamente e não devem ser enviados
    EscopoCobrado: normalizarValorOrcamento(data.escopoCobrado),
    DesenvolvimentoOrcado: normalizarValorOrcamento(data.desenvolvimentoOrcado),
    // Campos de gasto removidos - são calculados dinamicamente e não devem ser enviados
    DesenvolvimentoCobrado: normalizarValorOrcamento(data.desenvolvimentoCobrado),
    TestesOrcado: normalizarValorOrcamento(data.testesOrcado),
    // Campos de gasto removidos - são calculados dinamicamente e não devem ser enviados
    TestesCobrado: normalizarValorOrcamento(data.testesCobrado),
    // Campos de gasto removidos - são calculados dinamicamente e não devem ser enviados
    HomologacaoCobrado: normalizarValorOrcamento(data.homologacaoCobrado),
    // Campos de gasto removidos - são calculados dinamicamente e não devem ser enviados
    SaldoFinal: normalizarValorOrcamento(data.saldoFinal),
    DataUltimaAtualizacao: null,
    DataCriacao: null
  } : null

  return {
    Id: data.id || null,
    NumeroTicket: data.numeroTicket || null,
    Nome: data.nome || '',
    Descricao: data.descricao || null,
    PrefClockify: data.prefClockify ? data.prefClockify.toUpperCase() : null,
    TicketUrl: data.ticketUrl || null,
    IdProjetoClockify: data.idProjetoClockify || null,
    
    // Datas do projeto
    DataRecebimento: toRequiredDate(data.dataRecebimento),
    DataReferenciaValorHora: data.dataReferenciaValorHora
      ? toRequiredDate(data.dataReferenciaValorHora)
      : null,
    DataEnvioEstimativa: toBackendDate(data.dataEnvioEstimativa),
    DataAprovacaoEstimativa: toBackendDate(data.dataAprovacaoEstimativa),
    DataEnvioEscopo: toBackendDate(data.dataEnvioEscopo),
    DataAprovacaoEscopo: toBackendDate(data.dataAprovacaoEscopo),
    DataEnvioOrcamento: toBackendDate(data.dataEnvioOrcamento),
    DataAprovacaoOrcamento: toBackendDate(data.dataAprovacaoOrcamento),
    DataPublicacaoPreProd: null,
    DataPublicacaoProd: null,
    DataAtualizacao: null,
    DataUltimaAtualizacao: null,
    DataEstimativa: null,
    DataDesenvolvimento: null,
    DataTestesInternos: null,
    DataHomologacao: null,
    DataTestesProduto: null,
    
    // Relacionamentos
    TipoProjetoCodigo: toNumber(data.tipoProjetoCodigo) ?? 0,
    StatusCodigo: toNumber(data.statusCodigo) ?? 0,
    PrioridadeCodigo: toNumber(data.prioridadeCodigo) ?? 0,
    SolicitanteId: toNumber(data.solicitanteId, null),
    AnalistaSMNId: data.analistaSMNId ? Number(data.analistaSMNId) : 0,
    AnalistaDDPId: data.analistaDDPId ? Number(data.analistaDDPId) : 0,
    DesenvolvedorId: toNumber(data.desenvolvedorId, null),
    SaldoFinal: toNumber(data.saldoFinal, 0),
    Observacao: data.observacao || null,
    FlagEspecial: (data.flagEspecial as any) === 'true' || data.flagEspecial === true,
    AplicaEstimativa: data.aplicaEstimativa ?? true,
    AplicaEscopo: data.aplicaEscopo ?? true,
    AplicaOrcamento: data.aplicaOrcamento ?? true,
    
    // Campos de nome para exibição
    StatusNome: null,
    PrioridadeNome: null,
    TipoProjetoNome: null,
    SolicitanteNome: null,
    AnalistaSMNNome: null,
    AnalistaDDPNome: null,
    DesenvolvedorNome: null,
    
    // Objetos aninhados
    Cronograma: cronograma,
    Orcamento: orcamento
  }
}
